import { FC } from 'react';
import type { VerificationStatus } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { getEvaluationRowResultText, useTotalRowFit } from '../model';
import { DownloadExpertReportButton } from './DownloadExpertReportButton';
import { EvaluationBlockResult } from './EvaluationBlockResult';
import { ExpertReportResultFilter } from './ExpertReportResultFilter';
import type { FlatRow } from './ExpertReportTab';
import { ExpertReportVerificationStatus } from './ExpertReportVerificationStatus';

type Props = {
  row: FlatRow;
  conversationId: number;
  expertReportId: number;
  /** Стадия проверки результата человеком; отсутствует, пока результата нет */
  verificationStatus?: VerificationStatus;
  /** Классы отступов строки — общие у итога и строк блоков */
  className?: string;
};

/**
 * Строка итога заключения: наименование, управление заключением (стадия проверки, скачивание,
 * отбор правил) и сам итог.
 *
 * Итог, которому не хватает ширины рядом с управлением, забирает строку себе — управление встаёт
 * отдельной строкой над ней, а значения итога переносятся в границах заключения. Помещающийся
 * итог стоит с управлением в одной строке; что из этого выбрать, решает {@link useTotalRowFit}.
 */
export const ExpertReportTotalRow: FC<Props> = ({
  row,
  conversationId,
  expertReportId,
  verificationStatus,
  className,
}) => {
  const { rowRef, measureRef, controlsRef, isStacked } = useTotalRowFit();

  const controls = (
    <span ref={controlsRef} className="flex shrink-0 items-center gap-2">
      {/* Стадия проверки — у итога: она у результата заключения одна на всё заключение,
          как и скачивание с отбором */}
      {verificationStatus && (
        <ExpertReportVerificationStatus
          conversationId={conversationId}
          expertReportId={expertReportId}
          status={verificationStatus}
        />
      )}
      <DownloadExpertReportButton conversationId={conversationId} expertReportId={expertReportId} />
      <ExpertReportResultFilter />
    </span>
  );

  return (
    <div className={cn('relative flex flex-col gap-1', className)}>
      {isStacked && <div className="flex justify-end">{controls}</div>}
      <div ref={rowRef} className="flex items-center gap-2">
        <span className="flex flex-1 items-center gap-1">{row.name}</span>
        {!isStacked && controls}
        <EvaluationBlockResult row={row} conversationId={conversationId} expertReportId={expertReportId} />
      </div>
      {/* Слой замера: наименование и итог в одну строку — их ширина и решает, помещается ли итог
          рядом с управлением. Ширина берётся от содержимого, поэтому строкой она не ограничена */}
      <span aria-hidden className="pointer-events-none invisible absolute inset-0 overflow-hidden">
        <span ref={measureRef} className="flex w-max items-center gap-2">
          <span>{row.name}</span>
          <span>{getEvaluationRowResultText(row)}</span>
        </span>
      </span>
    </div>
  );
};
